/**
 * Runtime configuration reader.
 *
 * Precedence, and why:
 *
 *   1. `window.__APP_CONFIG__` -- written by the container entrypoint at
 *      startup from container environment. This is how a deployed Atlas is
 *      configured, and it is why ONE image runs unchanged in every
 *      environment.
 *   2. `import.meta.env` -- local `vite dev` only, where no container and no
 *      entrypoint exist. Vite's envDir is the repository root and its
 *      envPrefix is `PUBLIC_`, so dev reads the SAME keys under the SAME
 *      names as the container does. There are no VITE_ duplicates.
 *   3. A safe default.
 *
 * Per-environment values are never compiled into the bundle. Baking them in
 * via VITE_* build args "works locally" -- compose passes them as build args --
 * and then breaks the moment the same image is deployed elsewhere, because a
 * static bundle cannot read pod environment. Treat any `import.meta.env`
 * reference outside this file as a bug.
 *
 * f17.3: the browser holds no Entra setting at all. Sign in with GFT is run by
 * the API, which the SPA only links to.
 */

export interface AppConfig {
  /** Base URL the BROWSER uses to reach the API. Never a service name. */
  apiBaseUrl: string;
  /** False when no base URL was supplied by either source. */
  isConfigured: boolean;
}

/**
 * The runtime keys, under exactly the names they carry in the repository-root
 * `.env` and in the frontend service's `environment:` block.
 */
interface RuntimeConfig {
  PUBLIC_API_URL?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfig;
  }
}

const runtime: RuntimeConfig =
  (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined) ?? {};

/** Treat an unsubstituted placeholder or blank string as "not configured". */
function value(injected: string | undefined, fallback: string): string {
  const candidate = injected?.trim();
  if (!candidate || candidate.startsWith('${')) {
    return fallback;
  }
  return candidate;
}

/**
 * The `import.meta.env` fallback, available ONLY in a dev build.
 *
 * `import.meta.env.DEV` is replaced by a literal `false` in a production
 * build, so this whole branch is dropped by the bundler. That matters: Vite's
 * envDir points at the repository-root .env, so without this guard a locally-
 * run `npm run build` would inline that developer's URLs into a production
 * bundle, while the Docker build (which never sees .env) would not. Two builds
 * of the same commit would differ.
 *
 * In a container there is no dev mode: configuration comes from config.js,
 * written at startup.
 */
function devFallback(key: keyof RuntimeConfig): string {
  if (!import.meta.env.DEV) return '';
  return (import.meta.env[key] as string | undefined) ?? '';
}

/** One lookup per key: config.js first, then the dev .env, under one name. */
function setting(key: keyof RuntimeConfig): string {
  return value(runtime[key], devFallback(key));
}

// Deliberately no hardcoded URL fallback.
//
// A baked-in default would (a) put an environment-specific address into the
// bundle, which is the very thing the runtime bridge exists to prevent, and
// (b) turn "nobody configured this" into a confusing connection attempt
// against whatever happens to be on the developer's own machine. An empty
// value is reported as unconfigured instead, and the shell says so.
const apiBaseUrl = setting('PUBLIC_API_URL');

export const appConfig: AppConfig = {
  apiBaseUrl,
  isConfigured: apiBaseUrl.length > 0,
};
